import PropTypes from 'prop-types';
import React from 'react';
import ProcessDataTable from 'components/core/ProcessDataTable';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import Toolbar from 'components/core/toolbar/Toolbar';
import EntityHeader from 'components/core/EntityHeader';
import RIEInput from 'components/core/editable/RIEInput';
import RIEUnits from 'components/core/editable/RIEUnits';
import RIEComboBox from 'components/core/editable/RIEComboBox';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import InfoMessages from 'components/core/help/InfoMessages';
import Constants from 'utils/Constants';
import ProcessUtils from 'utils/ProcessUtils';
import _ from 'lodash';


export default class ImportMappingsTable extends ProcessDataTable {
    static propTypes = {
        variableMappings: PropTypes.object,
        onAdd: PropTypes.func,
        onDelete: PropTypes.func,
        onPrefixChange: PropTypes.func,
        onRetrievedVariableChanged: PropTypes.func,
        onVariableUnitsChanged: PropTypes.func,
        onOutputMappingChanged: PropTypes.func,
        clearErrorMessage: PropTypes.func,
        dodVariableOptions: PropTypes.object,
        prefix: PropTypes.string,
        outputDatastreams: PropTypes.array
    };

    shouldComponentUpdate = (nextProps, nextState) => {
        //table was updating after onBlur function and the rerender would mess up the focus/click event
        //if this components state changed it needs to update, otherwise check and see if props changed
        //if props didn't change we don't want to update the table
        if (!_.isEqual(this.state, nextState)) {
            return true;
        }
        return !_.isEqual(this.props, nextProps);
    };

    getRowKey() {
        return 'datastreamVariable';
    }

    /**
     * Must be defined as an array of objects (not a map)
     */
    getTableRowData(props) {
        return Object.keys(props.variableMappings).map((key) => {
            return { datastreamVariable: key, retrievedVariable: props.variableMappings[key] };
        });
    }

    getOutputstreamVariableOptions = () => {
        const outputDatastreamName = this.props.outputDatastreams[0];

        let outputstreamVariables = [];
        if (ProcessUtils.getDatastreamVariablesForDod(outputDatastreamName)) {
            outputstreamVariables = Object.keys(ProcessUtils.getDatastreamVariablesForDod(outputDatastreamName));
        }

        const options = Constants.convertArrayToSelectOptions(outputstreamVariables);
        Constants.sortSelectOptions(options);
        return options;
    };

    displayOutputColumn = () => {
        return this.props.outputDatastreams.length > 0;
    };

    onRetrievedVariableChanged = (fieldPath, value) => {
        const key = fieldPath[0];
        this.props.onRetrievedVariableChanged(key, value);
    };

    onVariableUnitsChanged = (fieldPath, value) => {
        const key = fieldPath[0];
        this.props.onVariableUnitsChanged(key, value.value);
    };

    onOutputMappingChanged = (fieldPath, value) => {
        const key = fieldPath[0];
        this.props.onOutputMappingChanged(key, value.value);
    };

    renderEmptyDataMsg() {
        let colSpan = 4;
        if (this.displayOutputColumn()) {
            colSpan += 1;
        }
        return <tbody><tr><td colSpan={colSpan} style={{ textAlign: 'center' }}><i>No variables selected</i></td></tr></tbody>;
    }

    renderToolbar() {
        return (
            <EntityHeader
                title=""
                selectedItems={this.state.selectedItems}
                className="noshrink"
            >
                <Toolbar>
                    <ToolbarItem
                        title="Delete"
                        className="fa fa-times delete"
                        clickHandler={this.props.onDelete}
                        selectionDependent
                        enabled
                    />
                    <ToolbarItem
                        title="New"
                        className="fa fa-plus new"
                        clickHandler={this.props.onAdd}
                        enabled
                    />
                </Toolbar>
            </EntityHeader>
        );
    }

    renderWithHelpIcon = (label, message) => {
        return (
            <InfoBoxLabel label={label} message={message}/>
        );

    };

    renderRetrievedVariable = (datastreamVariable) => {
        const retrievedVariable = this.props.variableMappings[datastreamVariable].retrievedVariableName;
        return (
            <span style={{ whiteSpace: 'nowrap', minWidth: '200px' }}>
                <RIEInput
                    value={retrievedVariable}
                    change={this.onRetrievedVariableChanged}
                    fieldPath={[datastreamVariable]}
                    formDisplay
                />
            </span>
        );
    };

    renderUnits = (datastreamVariable) => {
        const units = this.props.variableMappings[datastreamVariable].units;
        return (
            <RIEUnits
                value={ProcessUtils.getUnitsSelectValue(units)}
                change={this.onVariableUnitsChanged}
                fieldPath={[datastreamVariable]}
                clearable={false}
                options={Constants.RETRIEVED_VARIABLE_UNITS}
                formDisplay
                minWidth="200px"
            />
        );
    };

    renderOutputMapping = (datastreamVariable) => {
        const value = Constants.convertValueToSelectValue(this.props.variableMappings[datastreamVariable].outputMapping);

        return (
            <span style={{ whiteSpace: 'nowrap', minWidth: '200px' }}>
                <RIEComboBox
                    value={value}
                    options={this.getOutputstreamVariableOptions()}
                    multi={false}
                    change={this.onOutputMappingChanged}
                    fieldPath={[datastreamVariable]}
                    creatable
                    minWidth="250px"
                />
            </span>
        );
    };

    renderAddPrefix = () => {
        let label = 'Add prefix...';
        if (this.props.prefix !== '') {
            label = 'Change prefix...';
        }
        return (
            <span>
                <InfoBoxLabel label="Import as name" message={InfoMessages.retrievedVariableName}/>
                <ToolbarItem
                    title={label}
                    className={'prefix'}
                    clickHandler={this.props.onPrefixChange}
                    enabled
                    textOnly
                />
            </span>
        );
    };

    getTableColumns() {
        const datastreamVariableColumn = {
            property: 'datastreamVariable',
            header: {
                label: 'From variable in file',
                formatters: [
                    label => { return this.renderWithHelpIcon(label, InfoMessages.inputDatastreamVariableName); }
                ],
                props: {
                    style: {
                        whiteSpace: 'nowrap',
                        paddingRight: '16px',
                        verticalAlign: 'top'
                    }
                }
            }
        };
        const retrievedVariableColumn = {
            property: 'datastreamVariable',
            header: {
                label: 'Import as name',
                formatters: [
                    label => { return this.renderAddPrefix(); }
                ],
                props: {
                    style: {
                        minWidth: '200px',
                        verticalAlign: 'top'
                    }
                }
            },
            cell: {
                formatters: [
                    datastreamVariable => { return this.renderRetrievedVariable(datastreamVariable); }
                ]
            }
        };
        const unitsColumn = {
            property: 'datastreamVariable',
            header: {
                label: 'Import with units',
                formatters: [
                    label => { return this.renderWithHelpIcon(label, InfoMessages.retrievedVariableUnits); }
                ],
                props: {
                    style: {
                        minWidth: '130px',
                        verticalAlign: 'top'
                    }
                }
            },
            cell: {
                formatters: [
                    datastreamVariable => { return this.renderUnits(datastreamVariable); }
                ]
            }
        };
        const outputMappingColumn = {
            property: 'datastreamVariable',
            header: {
                label: 'Populate output datsatream variable',
                formatters: [
                    label => { return this.renderWithHelpIcon(label, InfoMessages.outputMapping); }
                ],
                props: {
                    style: {
                        minWidth: '200px',
                        verticalAlign: 'top'
                    }
                }
            },
            cell: {
                formatters: [
                    datastreamVariable => { return this.renderOutputMapping(datastreamVariable); }
                ]
            }
        };

        const columns = [datastreamVariableColumn, retrievedVariableColumn, unitsColumn];
        if (this.displayOutputColumn()) {
            columns.push(outputMappingColumn);
        }

        return columns;
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/ImportMappingsTable.js